import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { BarChart3, RefreshCw } from "lucide-react";

function fmtMXN(v) {
  if (v == null || isNaN(v)) return "$0.00";
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v);
}

export default function OpsMantenimientoReportes() {
  const { user } = useAuth();
  const [tab, setTab] = useState("branch");
  const [byBranch, setByBranch] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [bySupplier, setBySupplier] = useState([]);
  const [loading, setLoading] = useState(true);

  async function loadAll() {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/ops/maintenance/reports/by-branch"),
        api.get("/ops/maintenance/reports/by-category"),
        api.get("/ops/maintenance/reports/by-supplier"),
      ]);
      setByBranch(r1.data || []);
      setByCategory(r2.data || []);
      setBySupplier(r3.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  const tabs = [
    { key: "branch", label: "Por sucursal" },
    { key: "category", label: "Por categoria" },
    { key: "supplier", label: "Por proveedor" },
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-yellow-500" />
            Reportes de mantenimiento
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Analisis de solicitudes y gasto</p>
        </div>
        <button
          onClick={loadAll}
          data-testid="mnt-reports-refresh"
          className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl mb-6 w-fit">
        {tabs.map(t => (
          <button
            key={t.key}
            data-testid={`mnt-report-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Cargando...</div>
      ) : (
        <>
          {tab === "branch" && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="mnt-report-branch">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Sucursal</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Total</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Completadas</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">En proceso</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Rechazadas</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Total gastado</th>
                  </tr>
                </thead>
                <tbody>
                  {byBranch.map(r => (
                    <tr key={r.branch_id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{r.branch_name}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{r.total}</td>
                      <td className="px-4 py-3 text-right text-emerald-600 font-medium">{r.completadas}</td>
                      <td className="px-4 py-3 text-right text-blue-600">{r.en_proceso}</td>
                      <td className="px-4 py-3 text-right text-red-500">{r.rechazadas + (r.canceladas || 0)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">{fmtMXN(r.total_gastado)}</td>
                    </tr>
                  ))}
                  {byBranch.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-400">Sin datos</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "category" && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="mnt-report-category">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Categoria</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Total</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Completadas</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Total gastado</th>
                  </tr>
                </thead>
                <tbody>
                  {byCategory.sort((a, b) => b.total - a.total).map(r => (
                    <tr key={r.category} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{r.label}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{r.total}</td>
                      <td className="px-4 py-3 text-right text-emerald-600 font-medium">{r.completadas}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">{fmtMXN(r.total_gastado)}</td>
                    </tr>
                  ))}
                  {byCategory.length === 0 && (
                    <tr><td colSpan={4} className="text-center py-8 text-slate-400">Sin datos</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "supplier" && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="mnt-report-supplier">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Proveedor</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Cotizaciones aprobadas</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Total pagado</th>
                  </tr>
                </thead>
                <tbody>
                  {bySupplier.sort((a, b) => b.total_pagado - a.total_pagado).map(r => (
                    <tr key={r.supplier_id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{r.supplier_name}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{r.total_aprobadas}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">{fmtMXN(r.total_pagado)}</td>
                    </tr>
                  ))}
                  {bySupplier.length === 0 && (
                    <tr><td colSpan={3} className="text-center py-8 text-slate-400">Sin datos de pagos completados</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
